import { useState, useMemo } from "react";
import { useParams, Link } from "react-router";
import { SEO } from "@/components/SEO";
import { ArticleCard } from "@/components/home/ArticleCard";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { OptimizedImage } from "@/components/OptimizedImage";
import { type Category } from "@/data/articles";
import { useArticlesData } from "@/hooks/useArticlesData";
import { useCategories } from "@/hooks/useCategories";
import { ArticleGridSkeleton } from "@/components/skeletons/ArticleCardSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, ChevronRight, Grid3X3, Home } from "lucide-react";
import { motion } from "framer-motion";
import { getCategoryTheme } from "@/lib/categoryTheme";

const fallbackMeta: Record<string, { title: string; description: string }> = {
  lifestyle: {
    title: "Lifestyle",
    description:
      "Tips and inspiration for better living, wellness, and personal style.",
  },
  entertainment: {
    title: "Entertainment",
    description: "Latest movies, music, TV shows, and celebrity news.",
  },
  business: {
    title: "Business",
    description: "Business insights, entrepreneurship, and economic trends.",
  },
  sports: {
    title: "Sports",
    description:
      "Sports news, analysis, and athlete stories from around the world.",
  },
  health: {
    title: "Health",
    description: "Expert health tips, wellness routines, and medical insights.",
  },
  food: {
    title: "Food",
    description:
      "Recipes, restaurant reviews, culinary trends, and food culture.",
  },
  technology: {
    title: "Technology",
    description: "Latest gadgets, software, AI, and tech industry news.",
  },
  travel: {
    title: "Travel",
    description: "Explore destinations, travel guides, and adventure stories.",
  },
  furniture: {
    title: "Furniture",
    description:
      "Interior design, home decor, and furniture trends for modern living.",
  },
  // Legacy
  fashion: {
    title: "Fashion",
    description: "Discover all the trending fashion and beauty updates.",
  },
  "home-decor": {
    title: "Home Decor",
    description: "Interior design tips and home decoration ideas.",
  },
};

const ARTICLES_PER_PAGE = 6;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug ?? "";
  const [pageState, setPageState] = useState(() => ({
    slug: currentSlug,
    page: 1,
  }));
  const page = pageState.slug === currentSlug ? pageState.page : 1;
  const setPage = (updater: (page: number) => number) => {
    setPageState((prev) => ({
      slug: currentSlug,
      page: updater(prev.slug === currentSlug ? prev.page : 1),
    }));
  };
  const { loading, getByCategory } = useArticlesData();
  const { categories, loading: catsLoading } = useCategories();

  const category = slug as Category;
  const dbCategory = categories.find((c) => c.slug === slug);
  const meta = dbCategory
    ? {
        title: dbCategory.name,
        description:
          dbCategory.description ||
          `Explore the latest stories, trends, and guides in ${dbCategory.name} on Bloxida.`,
      }
    : fallbackMeta[slug ?? ""];

  const allArticles = useMemo(
    () => getByCategory(category),
    [getByCategory, category],
  );
  const categoryTheme = getCategoryTheme(slug);
  const accentColor = categoryTheme.color;
  const CategoryIcon = categoryTheme.Icon;

  if (loading || catsLoading) {
    return (
      <div className="container py-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-2/3" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="aspect-video rounded-lg" />
          <Skeleton className="aspect-video rounded-lg" />
        </div>
        <ArticleGridSkeleton count={6} cols="sm:grid-cols-2 lg:grid-cols-3" />
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="container py-20 text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">
          Category not found
        </h1>
        <Link to="/" className="text-primary hover:underline">
          Back to Home
        </Link>
      </div>
    );
  }

  const featured = allArticles.slice(0, 2);
  const remaining = allArticles.slice(2);
  const totalPages = Math.max(
    1,
    Math.ceil(remaining.length / ARTICLES_PER_PAGE),
  );
  const paginatedArticles = remaining.slice(0, page * ARTICLES_PER_PAGE);

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: typeof window !== "undefined" ? window.location.origin : "",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: typeof window !== "undefined" ? `${window.location.origin}/` : "",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: meta.title,
        item: typeof window !== "undefined" ? window.location.href : "",
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${meta.title} — Bloxida`}
        description={meta.description}
        jsonLd={breadcrumbJsonLd}
      />

      <motion.section 
        className="relative overflow-hidden border-b bg-card"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        {dbCategory?.image && (
          <div className="absolute inset-0 opacity-25">
            <OptimizedImage
              src={dbCategory.image}
              alt={dbCategory.name}
              priority
              className="w-full h-full object-cover"
              aspectClassName="absolute inset-0"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/95 to-background" />

        <div className="container relative py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-8">
            <Link to="/" className="hover:text-foreground transition-colors flex items-center gap-1">
              <Home className="h-3.5 w-3.5" /> Bloxida
            </Link>
            <ChevronRight className="h-3 w-3" />
            <Link
              to="/categories"
              className="hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Grid3X3 className="h-3.5 w-3.5" /> Categories
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium flex items-center gap-1">
              <CategoryIcon className="h-3.5 w-3.5" style={{ color: accentColor }} /> {meta.title}
            </span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-end">
            <div className="max-w-3xl">
              <div
                className="mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
                style={{ background: categoryTheme.badgeGradient }}
              >
                <CategoryIcon className="h-3.5 w-3.5" />
                {allArticles.length}{" "}
                {allArticles.length === 1 ? "article" : "articles"}
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground mb-4 flex items-center gap-3">
                <span
                  className="inline-flex h-12 w-12 items-center justify-center rounded-2xl shadow-lg"
                  style={{ background: categoryTheme.gradient }}
                >
                  <CategoryIcon className="h-7 w-7 text-white" />
                </span>
                {meta.title}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                {categoryTheme.description || meta.description}
              </p>
            </div>
            <Link
              to="/categories"
              className="inline-flex h-11 min-h-[44px] items-center justify-center gap-2 rounded-xl border bg-background px-5 text-sm font-semibold text-foreground hover:border-primary/40 hover:text-primary transition-all active:scale-95 shadow-sm"
            >
              <Grid3X3 className="h-4 w-4" />
              All categories
            </Link>
          </div>
        </div>
      </motion.section>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      >
      {allArticles.length === 0 ? (
        <div className="container py-12 text-center">
          <p className="text-muted-foreground mb-4">
            No articles in this category yet.
          </p>
          <Link to="/" className="text-primary hover:underline text-sm">
            Back to Home
          </Link>
        </div>
      ) : (
        <>
          {featured.length > 0 && (
            <section className="container py-8">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-foreground">
                    Featured in {meta.title}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Start with the newest editor picks.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {featured.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    variant="overlay"
                    className="aspect-[16/10] shadow-sm hover:shadow-lg transition-shadow duration-300"
                    priority
                  />
                ))}
              </div>
            </section>
          )}

          <section className="container pb-12">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-foreground">
                  All {meta.title} stories
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Showing {paginatedArticles.length} of{" "}
                  {Math.max(remaining.length, 0)} more stories.
                </p>
              </div>
            </div>

            {remaining.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8">
                  {paginatedArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>

                {page < totalPages && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      className="inline-flex h-11 items-center gap-2 px-6 text-sm font-semibold border rounded-lg hover:border-primary/40 hover:text-primary hover:bg-primary/5 transition-colors text-foreground"
                    >
                      Load More
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </>
            ) : featured.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                No additional articles in this category yet.
              </p>
            ) : null}

            <div className="mt-12">
              <NewsletterSignup source={`category:${slug}`} />
            </div>
          </section>
        </>
      )}
      </motion.div>
    </>
  );
}
